import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Lock, Mail, ArrowLeft } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { safeNext } from '../../utils/helpers'
import { Button, Input } from '../../components/common'

const WorkersLogin = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isResetMode, setIsResetMode] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  const { login, sendPasswordReset, loading, error } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const next = safeNext(searchParams.get('next'))

  const handleLogin = async (e) => {
    e.preventDefault()
    try {
      await login(email, password)
      navigate(next, { replace: true })
    } catch (err) {
      // Error is handled by AuthContext
    }
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    try {
      await sendPasswordReset(email)
      setResetSent(true)
    } catch (err) {
      // Error is handled by AuthContext
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-dark to-secondary-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-secondary-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        className="relative w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to="/"
          className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Website
        </Link>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <img
              src="/images/logo.png"
              alt="DLBC Clarksville"
              className="w-20 h-20 object-contain mx-auto mb-4"
            />
            <h1 className="text-2xl font-heading font-bold text-dark-100">
              {isResetMode ? 'Reset Password' : 'Workers Portal'}
            </h1>
            <p className="text-gray-500 mt-2">
              {isResetMode
                ? 'Enter your email to receive a reset link'
                : 'Sign in to view your schedule and resources'}
            </p>
          </div>

          {resetSent ? (
            <motion.div
              className="text-center py-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-dark-100 mb-2">Check Your Email</h3>
              <p className="text-gray-600 mb-6">
                We've sent a password reset link to {email}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setIsResetMode(false)
                  setResetSent(false)
                }}
              >
                Back to Login
              </Button>
            </motion.div>
          ) : isResetMode ? (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <Input
                label="Email Address"
                type="email"
                icon={Mail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <Button type="submit" variant="primary" fullWidth loading={loading}>
                Send Reset Link
              </Button>

              <button
                type="button"
                onClick={() => setIsResetMode(false)}
                className="w-full text-center text-primary-600 hover:text-primary-700 font-medium"
              >
                Back to Login
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-6">
              <Input
                label="Email Address"
                type="email"
                icon={Mail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />

              <Input
                label="Password"
                type="password"
                icon={Lock}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <Button type="submit" variant="primary" fullWidth loading={loading}>
                Sign In
              </Button>

              <div className="space-y-2 text-center">
                <button
                  type="button"
                  onClick={() => setIsResetMode(true)}
                  className="w-full text-primary-600 hover:text-primary-700 font-medium"
                >
                  Forgot your password?
                </button>
                <p className="text-gray-500 text-sm">
                  New worker?{' '}
                  <Link
                    to="/workers/register"
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Create an account
                  </Link>
                </p>
              </div>
            </form>
          )}
        </div>

        <p className="text-center text-white/50 text-sm mt-6">
          © {new Date().getFullYear()} Deeper Life Bible Church Clarksville
        </p>
      </motion.div>
    </div>
  )
}

export default WorkersLogin
